import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Paper,
  Box,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { obtenerDataImagen } from '../../actions/ImagenAction';
import { actualizarUsuario } from '../../actions/UsuariosAction';
import { useStateValue } from '../../contexto/store';

const PerfilUsuario = () => {
  const [{ sesionUsuario }, dispatch] = useStateValue();
  const [usuario, setUsuario] = useState({
    nombrecompleto: '',
    email: '',
    userName: '',
    password: '',
    confirmarpassword: '',
    imagenPerfil: null, // Guardaremos el base64 aquí
    fotoUrl: '', // URL para mostrar la imagen
    numerotelefono: '',
  });

  // --- Manejo de Dropzone para la Carga de Imágenes ---
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const fotoUrl = URL.createObjectURL(file); // Crea una URL para previsualizar
        obtenerDataImagen(file).then((base64Image) => {
          setUsuario((anterior) => ({
            ...anterior,
            imagenPerfil: base64Image, // Guarda el base64
            fotoUrl: fotoUrl, // Guarda la URL para mostrar
          }));
        });
      }
    },
    []
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.png', '.gif', '.jpg'],
    },
    multiple: false, // Solo permite una imagen
    maxSize: 5242880, // 5MB
  });
  // --- FIN Manejo de Dropzone ---

  const ingresarValoresMemoria = (e) => {
    const { name, value } = e.target;
    setUsuario((anterior) => ({
      ...anterior,
      [name]: value,
    }));
  };

  const guardarUsuario = async (e) => { // Agregamos async para usar await
    e.preventDefault();

    if (
      usuario.password === '' ||
      usuario.confirmarpassword === '' ||
      usuario.password === undefined ||
      usuario.confirmarpassword === undefined
    ) {
      dispatch({
        type: 'OPEN_SNACKBAR',
        openMensaje: {
          open: true,
          mensaje: 'El password o el confirmar no pueden estar vacios',
        },
      });
      return; // Detenemos la ejecución
    }

    if (usuario.password.trim() !== usuario.confirmarpassword.trim()) {
      dispatch({
        type: 'OPEN_SNACKBAR',
        openMensaje: {
          open: true,
          mensaje: 'Las contraseñas son distintas',
        },
      });
      return; // Detenemos la ejecución
    }

    try {
        const response = await actualizarUsuario(usuario, dispatch);
        if (response.status === 200) {
            dispatch({
                type: 'OPEN_SNACKBAR',
                openMensaje: {
                    open: true,
                    mensaje: 'Se guardó exitosamente el Usuario',
                },
            });
            window.localStorage.setItem('token_seguridad', response.data.token);
            // Si la API devuelve la URL de la imagen actualizada, úsala
            // De lo contrario, mantén la fotoUrl que ya tienes en el estado local
            setUsuario((anterior) => ({
                ...anterior,
                password: '',
                confirmarpassword: '',
                imagenPerfil: null, // Limpiar el base64 después de guardar
                // fotoUrl: response.data.usuario.imagenPerfil || anterior.fotoUrl, // O la URL real si la API la devuelve
            }));
        } else {
            dispatch({
                type: 'OPEN_SNACKBAR',
                openMensaje: {
                    open: true,
                    mensaje: `Error al intentar guardar: ${response.data.mensaje || 'Error desconocido'}`,
                },
            });
        }
    } catch (error) {
        console.error("Error al actualizar usuario:", error);
        dispatch({
            type: 'OPEN_SNACKBAR',
            openMensaje: {
                open: true,
                mensaje: 'Ocurrió un error inesperado al guardar.',
            },
        });
    }
  };

  useEffect(() => {
    // Asegúrate de que sesionUsuario y sesionUsuario.usuario existan
    if (sesionUsuario && sesionUsuario.usuario) {
      setUsuario({
        ...sesionUsuario.usuario, // Carga los datos del usuario de la sesión
        confirmarpassword: '',
        password: '',
        imagenPerfil: null, // Siempre inicia como null, se llena al subir una nueva
        fotoUrl: sesionUsuario.usuario.imagenPerfil || '', // Usa la imagen actual si existe
        numerotelefono: sesionUsuario.usuario.numerotelefono || '',
      });
    }
  }, [sesionUsuario]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header con gradiente */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <AccountCircleIcon sx={{ fontSize: 32, color: 'white' }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
            Mi Perfil
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
            Administra tu información personal
          </Typography>
        </Box>
      </Paper>

      {/* Contenido del perfil */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Avatar Section */}
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'grey.50',
          }}
        >
          <Avatar
            src={usuario.fotoUrl}
            sx={{
              width: 120,
              height: 120,
              border: '4px solid',
              borderColor: 'primary.main',
              boxShadow: 3,
            }}
          >
            <PersonIcon sx={{ fontSize: 64 }} />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            {usuario.nombrecompleto || 'Usuario'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {usuario.email}
          </Typography>
        </Box>

        <Divider />

        {/* Form Section */}
        <Box sx={{ p: 4 }}>
          <form noValidate>
            <Grid container spacing={3}>
              {/* Nombre completo */}
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  name="nombrecompleto"
                  value={usuario.nombrecompleto}
                  onChange={ingresarValoresMemoria}
                  variant="outlined"
                  fullWidth
                  label="Nombre y Apellido"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Username */}
              <Grid item xs={12} md={6}>
                <TextField
                  disabled
                  name="userName"
                  value={usuario.userName}
                  onChange={ingresarValoresMemoria}
                  variant="outlined"
                  fullWidth
                  label="Nombre de Usuario"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircleIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="email"
                  value={usuario.email}
                  onChange={ingresarValoresMemoria}
                  variant="outlined"
                  fullWidth
                  label="Correo Electrónico"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Teléfono */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="numerotelefono"
                  value={usuario.numerotelefono}
                  onChange={ingresarValoresMemoria}
                  variant="outlined"
                  fullWidth
                  label="Número de Teléfono"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Nueva contraseña */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="password"
                  value={usuario.password}
                  onChange={ingresarValoresMemoria}
                  type="password"
                  variant="outlined"
                  fullWidth
                  label="Nueva Contraseña"
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Confirmar contraseña */}
              <Grid item xs={12} md={6}>
                <TextField
                  name="confirmarpassword"
                  value={usuario.confirmarpassword}
                  onChange={ingresarValoresMemoria}
                  type="password"
                  variant="outlined"
                  fullWidth
                  label="Confirmar Contraseña"
                  autoComplete="new-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'action.active' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              {/* Dropzone para imagen */}
              <Grid item xs={12}>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: 'grey.50',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'primary.lighter',
                      borderColor: 'primary.dark',
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon
                    sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
                  />
                  <Typography variant="body1" fontWeight={600} gutterBottom>
                    Arrastra y suelta tu foto aquí, o haz clic para seleccionarla
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Solo imágenes (JPG, PNG, GIF) - Máximo 5MB
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Botón guardar */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={guardarUsuario}
                sx={{
                  minWidth: 200,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Guardar Cambios
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Container>
  );
};

export default PerfilUsuario;
